import React from 'react';
import base64 from './base64';
import useLocale, { locale as I18N } from './i18n';
import env from '../env.json';
import { getOperation, login } from '../api/admin';
import { intersection } from 'lodash';
import { MDBBtn, MDBInput, MDBModal, MDBModalBody, MDBModalContent, MDBModalDialog, MDBModalHeader } from 'mdb-react-ui-kit';
import { CaptchaData, getCaptcha } from '../api/resource';
import Toast from '../component/toast';

type Payload = {
    admin: number;
    package: string;
    nickname: string;
    avatar: string;
    role: number;
    version: number;
    exp: number;
};

var token: string | null = null;
var payload: Payload | null = null;

function getToken() {
    return new Promise<string>((resolve, reject) => {
        if (token === null) {
            let v = typeof localStorage !== 'undefined' && localStorage.getItem('token');
            if (v) {
                token = v;
                payload = JSON.parse(base64.decode(token.split('.')[1]) || '{}');
                checkPayload(payload) ? resolve(token) : reject(token);
            } else {
                token = '';
                resolve(token);
            };
        } else if (token) {
            checkPayload(payload) ? resolve(token) : reject(token);
        } else {
            resolve(token);
        }
    });
}

function checkPayload(payload: Payload | null) {
    return payload && payload.exp > (new Date()).getTime() / 1000;
}

function setToken(access: string, refresh: string) {
    return new Promise<Payload>((resolve, reject) => {
        if (access) {
            token = access;
            payload = JSON.parse(base64.decode(token.split('.')[1]) || 'null') as Payload;
            typeof localStorage !== 'undefined' && localStorage.setItem('refresh_token', refresh);
            typeof localStorage !== 'undefined' && localStorage.setItem('token', access);
            resolve(payload);
        } else {
            token = '';
            reject();
        }
    });
}

function delToken(): void {
    token = '';
    payload = null;
    typeof localStorage !== 'undefined' && localStorage.removeItem('token');
    typeof window !== 'undefined' && window.location.reload();
}

function getPayload(): Payload | null {
    if (!payload && (token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : '')) {
        try {
            payload = JSON.parse(base64.decode(token.split('.')[1]) || '{}');
            if (!checkPayload(payload)) {
                if (token) {
                    refreshToken(token);
                } else {
                    payload = null;
                    token = '';
                }
            }
        } catch {
            payload = null;
            token = '';
        }
    }
    return payload;
}

const PayloadContext = React.createContext<[Payload | null, (access: string, refresh: string) => void, () => void]>([null, () => { }, () => { }]);
const RbacContext = React.createContext<[(operation: string | Array<string>) => boolean, () => void]>([() => true, () => { }]);

function RbacProvider(props: { children: React.ReactNode }) {
    const {0: granted, 1: setGranted} = React.useState<Array<string>>([]);
    const {0: payload} = React.useContext(PayloadContext);
    let isGranted = React.useCallback((operation: string | Array<string>) => {
        let ops = typeof operation === 'string' ? [operation] : operation;
        return (payload?.role || 0) === 128 || intersection(granted, ops).length === ops.length;
    }, [payload, granted]);
    let doLoad = React.useCallback(() => {
        getOperation({ filter: { granted: 1 }, page: 1, limit: 1000 }).then(response => {
            if (typeof response === 'object') {
                let result: string[] = [];
                for (let i of response.data) {
                    result.push(i.code);
                    result.push(i.alias);
                }
                setGranted(result);
            }
        });
    }, [setGranted]);
    React.useEffect(() => {
        if (granted.length === 0 && (payload?.role || 0) > 128) {
            doLoad();
        }
    }, [payload, granted, doLoad]);
    return (
        <RbacContext.Provider value={[isGranted, doLoad]}>
            {props.children}
        </RbacContext.Provider>
    );
}

export function PayloadProvider(props: { children: React.ReactNode }) {
    const { 0: __ } = useLocale();
    const { 0: payload, 1: setPayload } = React.useState<Payload | null>(getPayload());
    const { 0: expired, 1: setExpired } = React.useState(false);
    const set = React.useCallback((access: string, refresh: string) => {
        setToken(access, refresh).then(setPayload);
        setExpired(false);
    }, [setPayload, setExpired]);
    const del = React.useCallback(() => {
        delToken();
        setPayload(null);
        localStorage.clear();
        sessionStorage.clear();
        window.location.reload();
    }, [setPayload]);
    React.useEffect(() => {
        if (payload) {
            const check = () => {
                if (Date.now() / 1000 >= payload.exp - 60) {
                    const token = localStorage.getItem('token');
                    token && refreshToken(token).catch(() => {
                        setExpired(true);
                    });
                }
            };
            let i = setInterval(check, 60000);
            const l = setExpired.bind(null, true);
            document.addEventListener('token.expired', l);
            const v = () => {
                if (document.visibilityState === 'hidden') {
                    i && clearInterval(i);
                } else {
                    check();
                    i = setInterval(check, 60000);
                }
            };
            document.addEventListener('visibilitychange', v);
            return () => {
                document.removeEventListener('token.expired', l);
                document.removeEventListener('visibilitychange', v);
            };
        }
    }, [payload, setExpired]);
    return (
        <PayloadContext.Provider value={[payload, set, del]}>
            <RbacProvider>
                {props.children}
                <MDBModal open={expired} tabIndex="-1">
                    <MDBModalDialog centered>
                        <MDBModalContent>
                            <MDBModalHeader>
                                <h5 className="mb-0">{__('The authentication has expired. Please sign in again.')}</h5>
                            </MDBModalHeader>
                            <MDBModalBody>
                                <LoginForm />
                            </MDBModalBody>
                        </MDBModalContent>
                    </MDBModalDialog>
                </MDBModal>
            </RbacProvider>
        </PayloadContext.Provider>
    );
}

const width = 180, height = 72;

export function LoginForm() {
    const { 1: setToken } = usePayload();
    const { 0: __ } = useLocale();
    const { 0: captcha, 1: setCaptcha } = React.useState<CaptchaData | null>(null);
    const loadCaptcha = React.useCallback((e?: React.MouseEvent<HTMLImageElement>) => {
        if (e) {
            e.stopPropagation();
            e.preventDefault();
        }
        getCaptcha({ width, height }).then(response => typeof response !== 'string' && setCaptcha(response));
    }, [setCaptcha]);
    const doLogin = React.useCallback<React.FormEventHandler<HTMLFormElement>>(e => {
        e.stopPropagation();
        e.preventDefault();
        if ((e.target as HTMLFormElement).checkValidity()) {
            let fd = new FormData(e.target as HTMLFormElement);
            login({ username: fd.get('username') as string, password: fd.get('password') as string, captcha_key: fd.get('captcha_key') as string, captcha: fd.get('captcha') as string }).then(response => {
                Toast.show(__('Login Successfully.'), 'success').then(() => typeof response !== 'string' && setToken(response.access_token, response.refresh_token));
            }, error => {
                loadCaptcha();
                Toast.show(typeof error === 'string' ? error : (error.error || error.message || __('An error detected. Please try again later.')), 'danger');
            });
        }
    }, [loadCaptcha, setToken, __]);
    React.useEffect(loadCaptcha, [loadCaptcha]);
    return (
        <form onSubmit={doLogin}>
            <MDBInput
                type="text"
                label={__("Username")}
                name="username"
                wrapperClass="mb-3"
                spellCheck={false}
                autoComplete="off"
                required
            />
            <MDBInput
                type="password"
                label={__("Password")}
                name="password"
                wrapperClass="mb-3"
                spellCheck={false}
                autoComplete="off"
                required
            />
            <div className="mb-3 dropdown">
                {captcha ? (
                    <>
                        <input type="hidden" name="captcha_key" value={captcha.key} />
                        <img src={captcha.image} className="captcha" width={width} height={height} alt="" onClick={loadCaptcha} />
                    </>
                ) : null}
                <MDBInput
                    label={__("Captcha")}
                    name="captcha"
                    spellCheck={false}
                    autoComplete="off"
                    required
                />
            </div>
            <MDBBtn type='submit' block>{__('Sign in')}</MDBBtn>
        </form>
    );
}

export function usePayload() {
    return React.useContext(PayloadContext);
}

export function useRbac() {
    return React.useContext(RbacContext);
}

let debounce = false;

export function refreshToken(access_token: string, retry = true): Promise<string> {
    return new Promise((resolve, reject) => {
        if (debounce) {
            let count = 0;
            let i = setInterval(() => {
                if (getPayload()?.package && token) {
                    clearInterval(i);
                    resolve(token);
                } else if (count++ >= 3) {
                    clearInterval(i);
                    reject();
                }
            }, 1000);
            return;
        }
        debounce = true;
        let refresh_token = typeof localStorage !== 'undefined' && localStorage.getItem('refresh_token');
        let fail = () => {
            typeof localStorage !== 'undefined' && localStorage.clear();
            debounce = false;
            window.location.reload();
            reject();
        };
        if (refresh_token) {
            let xhr = new XMLHttpRequest();
            xhr.open('POST', env.BASE_URL + 'token/');
            xhr.setRequestHeader('Content-Type', 'application/json');
            xhr.setRequestHeader('Accept-Language', I18N.lang);
            let rid = typeof localStorage !== 'undefined' && localStorage.getItem('rid');
            rid && xhr.setRequestHeader('X-Rid', rid);
            xhr.addEventListener('load', function () {
                let rid = xhr.getResponseHeader('X-Rid');
                rid && typeof localStorage !== 'undefined' && localStorage.setItem('rid', rid);
                if (xhr.status === 401 && retry) {
                    refreshToken(access_token, false).then(resolve, reject);
                    return;
                }
                let response: any = xhr.responseText;
                try {
                    response = JSON.parse(response);
                } catch (e) {
                    fail();
                }
                if (xhr.status < 400) {
                    setToken(response.access_token, response.refresh_token)
                    resolve(response.access_token);
                } else {
                    fail();
                }
                debounce = false;
            });
            xhr.send(JSON.stringify({ access_token, refresh_token }));
        } else {
            fail();
        }
    });
}

export { getPayload, getToken, setToken, delToken };
